import React, { Suspense, lazy, useRef } from 'react';
import { Skeleton } from '@/components/ui/skeleton';
import {
  RouterProvider,
  createBrowserRouter,
  useLocation,
} from 'react-router-dom';
import {
  AppWrapper,
  DashboardWrapper,
  OperatorWrapper,
} from '@/components/wrappers';

// Core (always needed)
import ErrorPage from '@/routes/error';
import ErrorBoundary from '@/errors/ErrorBoundary';
import { isFeatureEnabled } from '@/config/featureFlags';

// Eagerly loaded: landing & auth shell (first screens users see)
import Home from '@/pages/home/home';
import AuthPage from '@/pages/auth/index';
import AdminAuth from '@/pages/admin/auth';
import AppView from '@/pages/app/index';
import Dashboard from '@/pages/admin/dashboard/index';

// Lazy-loaded: app pages
const About = lazy(() => import('@/pages/sharings/about'));
const Submits = lazy(() => import('@/pages/app/submission/submission'));
const EditProfile = lazy(() => import('@/pages/app/editProfile'));
const ViewProfile = lazy(() => import('@/pages/app/viewProfile'));
const Chatmission = lazy(() => import('@/pages/app/chat/chatmission'));
const Certification = lazy(
  () => import('@/pages/app/certification/certification'),
);
const NewCertification = lazy(
  () => import('@/pages/app/certification/newCertification'),
);
const EditCertification = lazy(
  () => import('@/pages/app/certification/editCertification'),
);
const ViewCertification = lazy(
  () => import('@/pages/app/certification/viewCertification'),
);
const DoneCertificate = lazy(
  () => import('@/pages/app/certification/doneCertificate'),
);
const CodeRequestPage = lazy(() => import('@/pages/app/codeRequest/code'));
const CodeApplication = lazy(
  () => import('@/pages/app/codeRequest/codeApplication'),
);
const NewCode = lazy(() => import('@/pages/app/codeRequest/newCode'));
const EditCode = lazy(() => import('@/pages/app/codeRequest/editCode'));
const ViewCode = lazy(() => import('@/pages/app/codeRequest/viewCode'));
const DoneCode = lazy(() => import('@/pages/app/codeRequest/doneCode'));
const CodesOfTNV = lazy(() =>
  import('@/pages/app/codesOfTnv').then(m => ({ default: m.CodesOfTNV })),
);
const Facture = lazy(() =>
  import('@/pages/app/facture').then(m => ({ default: m.Facture })),
);
const Certificate = lazy(() =>
  import('@/pages/app/pcode').then(m => ({ default: m.Certificate })),
);

// Lazy-loaded: dashboard pages
const Requests = lazy(
  () => import('@/pages/admin/dashboard/requests/requests'),
);
const CertificationRequests = lazy(
  () =>
    import('@/pages/admin/dashboard/certificationRequest/certificationRequests'),
);
const AdminArea = lazy(() => import('@/pages/admin/dashboard/chat/adminArea'));
const CodeRequest = lazy(
  () => import('@/pages/admin/dashboard/codeRequest/codeRequests'),
);
const ViewProfileAdmin = lazy(
  () => import('@/pages/admin/dashboard/viewProfile'),
);
const EditProfileAdmin = lazy(
  () => import('@/pages/admin/dashboard/editProfile'),
);

// Lazy-loaded: sharings
const News = lazy(() => import('@/pages/sharings/news/news'));
const CustomsPostAdresses = lazy(
  () => import('@/pages/app/customsPostAdresses/CustomsPostAdresses'),
);
const NewsItem = lazy(() => import('@/pages/sharings/news/newsItem'));
const Faq = lazy(() => import('@/pages/app/faq/Faq'));

const useNormalizedErrors = () => isFeatureEnabled('useNormalizedErrors');

function RouteBoundary({
  component: Component,
  boundaryName,
  componentProps = {},
}) {
  const location = useLocation();
  if (!useNormalizedErrors()) {
    return <Component {...componentProps} />;
  }
  return (
    <ErrorBoundary boundaryName={boundaryName} resetKeys={[location.pathname]}>
      <Component {...componentProps} />
    </ErrorBoundary>
  );
}

const Calculation = lazy(() => import('@/pages/home/calculation/Calculation'));
const CalculationContents = lazy(() =>
  import('@/pages/home/calculation/Calculation').then(module => ({
    default: module.CalculationContents,
  })),
);
const DirectoriesWrapper = lazy(
  () => import('@/pages/home/directories/DirectoriesWrapper'),
);
const Directories = lazy(() => import('@/pages/home/directories/Directories'));
const DocExamples = lazy(() => import('@/pages/app/docExamples/DocExamples'));
const CallCenter = lazy(() => import('@/pages/call-center/'));
const Clients = lazy(() => import('@/pages/call-center/pages/clients/Clients'));
const Callback = lazy(
  () => import('@/pages/call-center/pages/callback/Callback'),
);
const SingleClient = lazy(
  () => import('@/pages/call-center/pages/singleClient/single-client'),
);

export function root() {
  const elementRefs = {
    newsRef: useRef(null),
    aboutUsRef: useRef(null),
    contactsRef: useRef(null),
    partnersRef: useRef(null),
    faqRef: useRef(null),
    mapRef: useRef(null),
  };

  const scrollToElement = elementRef => {
    elementRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
      alignToTop: true,
    });
  };

  const withSuspense = (Component, props = {}) => (
    <Suspense
      fallback={
        <div className="space-y-4 p-6">
          <Skeleton className="h-10 w-full" />
          <div className="space-y-3">
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-3/4" />
          </div>
        </div>
      }
    >
      <Component {...props} />
    </Suspense>
  );

  const router = createBrowserRouter([
    {
      path: '/',
      element: (
        <Home elementRefs={elementRefs} scrollToElement={scrollToElement} />
      ),
      errorElement: <ErrorPage />,
    },
    {
      path: 'about',
      errorElement: <ErrorPage />,
      element: withSuspense(About),
    },
    {
      path: 'news',
      errorElement: <ErrorPage />,
      element: withSuspense(News),
    },
    {
      path: 'news/:id',
      errorElement: <ErrorPage />,
      element: withSuspense(RouteBoundary, {
        boundaryName: 'news-item-route',
        component: NewsItem,
      }),
    },
    {
      path: 'calculation',
      errorElement: <ErrorPage />,
      element: withSuspense(Calculation, {
        elementRefs,
        scrollToElement,
      }),
    },
    {
      path: 'directories',
      errorElement: <ErrorPage />,
      element: withSuspense(DirectoriesWrapper, {
        elementRefs,
        scrollToElement,
      }),
    },
    {
      path: 'auth',
      errorElement: <ErrorPage />,
      children: [
        {
          path: 'user',
          element: <AuthPage />,
        },
        {
          path: 'admin',
          element: <AdminAuth />,
        },
      ],
    },
    {
      path: 'app',
      errorElement: <ErrorPage />,
      element: (
        <AppWrapper>
          <AppView />
        </AppWrapper>
      ),
      children: [
        {
          path: 'submissions',
          element: withSuspense(Submits),
        },
        {
          path: 'certification',
          children: [
            {
              index: true,
              element: withSuspense(Certification),
            },
            {
              path: 'new',
              element: withSuspense(NewCertification),
            },
            {
              path: ':id',
              element: withSuspense(ViewCertification),
            },
            {
              path: ':id/edit',
              element: withSuspense(EditCertification),
            },
            {
              path: 'done',
              element: withSuspense(DoneCertificate),
            },
          ],
        },
        {
          path: 'codes',
          children: [
            {
              index: true,
              element: withSuspense(CodeRequestPage),
            },
            {
              path: 'application',
              element: withSuspense(CodeApplication),
            },
            {
              path: 'new',
              element: withSuspense(NewCode),
            },
            {
              path: ':id',
              element: withSuspense(ViewCode),
            },
            {
              path: ':id/edit',
              element: withSuspense(EditCode),
            },
            {
              path: 'done',
              element: withSuspense(DoneCode),
            },
          ],
        },
        {
          path: 'edit-profile',
          element: withSuspense(EditProfile),
        },
        {
          path: 'view-profile',
          element: withSuspense(ViewProfile),
        },
        {
          path: 'support',
          element: withSuspense(Chatmission),
        },
        {
          path: 'codes-of-tnv',
          element: withSuspense(CodesOfTNV),
        },
        {
          path: 'post_office',
          // element: <PostOffices />,
          element: withSuspense(CustomsPostAdresses),
        },
        {
          path: 'facture',
          element: withSuspense(Facture),
        },
        {
          path: 'pcode',
          element: withSuspense(Certificate),
        },
        {
          path: 'docExapmles',
          element: withSuspense(DocExamples),
        },
        {
          path: 'calc',
          element: withSuspense(CalculationContents),
        },
        {
          path: 'directory',
          element: withSuspense(Directories),
        },
        {
          path: 'faq',
          element: withSuspense(Faq),
        },
      ],
    },
    {
      path: 'dashboard',
      errorElement: <ErrorPage />,
      element: (
        <DashboardWrapper>
          <Dashboard />
        </DashboardWrapper>
      ),
      children: [
        {
          path: 'all-applications',
          element: withSuspense(Requests, { filter: 'all' }),
        },
        { path: 'view-profile', element: withSuspense(ViewProfileAdmin) },
        { path: 'edit-profile', element: withSuspense(EditProfileAdmin) },
        // {
        //   path: 'applications',
        //   element: <Requests filter={'in-process'} />,
        // },
        // {
        //   path: 'accepted-applications',
        //   element: <Requests filter={'accepted'} />,
        // },
        {
          path: 'certification',
          element: withSuspense(CertificationRequests, { filter: 'all' }),
        },
        {
          path: 'code',
          element: withSuspense(CodeRequest, { filter: 'all' }),
        },
        {
          path: 'support/*',
          element: withSuspense(AdminArea),
        },
        {
          path: 'directory',
          element: withSuspense(Directories),
        },
        {
          path: 'calc',
          element: withSuspense(CalculationContents),
        },
        {
          path: 'pcode',
          element: withSuspense(Certificate),
        },
        {
          path: 'facture',
          element: withSuspense(Facture),
        },
        {
          path: 'post_office',
          // element: <PostOffices />,
          element: withSuspense(CustomsPostAdresses),
        },
      ],
    },
    {
      path: 'callCenter',
      errorElement: <ErrorPage />,
      element: <OperatorWrapper>{withSuspense(CallCenter)}</OperatorWrapper>,
      index: withSuspense(Clients),
      children: [
        {
          path: 'allClients',
          element: withSuspense(Clients),
        },
        {
          path: 'allClients/:id',
          element: withSuspense(SingleClient),
        },
        {
          path: 'callback',
          element: withSuspense(Callback),
        },
      ],
    },
  ]);
  return <RouterProvider router={router} />;
}

// FIX THE STYLES
// REMOVE THE LOADERS
